// CO3 — camera look-ahead. A pure helper that biases the camera toward where the player is RUNNING, so you
// see more of what you're charging into (Megabonk-style lead) instead of the player sitting dead-centre.
// Returns a ground-plane (x,z) offset from the player. Render-only, deterministic, headless-testable.

export interface Vec2 {
  x: number
  z: number
}

/**
 * Lead offset from a velocity: points along the movement direction, scaled by how fast you're going up to
 * `lead` units. Below a small deadzone speed it returns zero (no jitter when standing still).
 * @param maxLead  furthest the camera leads ahead (world units)
 * @param speedRef speed at which the full lead is reached
 */
export function lookAheadOffset(vx: number, vz: number, maxLead = 2.6, speedRef = 7): Vec2 {
  const speed = Math.hypot(vx, vz)
  if (speed < 0.35) return { x: 0, z: 0 }
  const t = Math.min(1, speed / speedRef)
  const k = (maxLead * t) / speed
  return { x: vx * k, z: vz * k }
}

// FEAT-C — free orbit camera (Megabonk-style). At yaw 0 the camera sits behind the player on +z (the old
// fixed framing). cameraOrbit gives the camera's ground offset from the player for a given yaw; rotateIntent
// rotates the raw move input into world space so "screen-forward" (W / stick-up) is always away from the
// camera regardless of yaw. Both share the same yaw convention so movement always matches the view.

/** Camera ground offset from the player at this yaw (yaw 0 = directly behind, on +z). */
export function cameraOrbit(yaw: number, dist: number): Vec2 {
  return { x: Math.sin(yaw) * dist, z: Math.cos(yaw) * dist }
}

/** Rotate a raw 2D move intent into world space for the current camera yaw (yaw 0 = identity). */
export function rotateIntent(mx: number, mz: number, yaw: number): Vec2 {
  const c = Math.cos(yaw)
  const s = Math.sin(yaw)
  return { x: mx * c + mz * s, z: -mx * s + mz * c }
}
